import * as THREE from "three";
import { Reflector } from "three/examples/jsm/objects/Reflector.js";

function canvasTex(w, h, draw) {
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  draw(c.getContext("2d"), w, h);
  const t = new THREE.CanvasTexture(c);
  t.anisotropy = 4;
  return t;
}

const vendingTex = (accent) =>
  canvasTex(256, 512, (g, w, h) => {
    g.fillStyle = "#0c0f12";
    g.fillRect(0, 0, w, h);
    g.fillStyle = accent;
    g.fillRect(0, 0, w, 70);
    g.fillStyle = "#ffffff";
    g.font = "bold 40px sans-serif";
    g.textAlign = "center";
    g.textBaseline = "middle";
    g.fillText("自販機", w / 2, 36);
    for (let r = 0; r < 4; r++) {
      for (let c = 0; c < 4; c++) {
        const x = 18 + c * 56;
        const y = 90 + r * 78;
        g.fillStyle = ["#e8f4ff", "#bfe3ff", "#ff5a5a", "#5aff9a", "#ffd25a"][(r * 4 + c) % 5];
        g.fillRect(x, y, 40, 60);
        g.fillStyle = "rgba(0,0,0,0.35)";
        g.fillRect(x, y + 44, 40, 16);
      }
    }
    g.fillStyle = "#1a1f24";
    g.fillRect(16, 420, w - 32, 70);
    g.fillStyle = "#ff3b30";
    g.fillRect(28, 436, 60, 12);
  });

export function buildProps(scene, disposables, camera, posterUrl = "/textures/magazine.jpg") {
  camera.layers.enable(1);
  const steel = new THREE.MeshStandardMaterial({ color: 0x3a444b, roughness: 0.45, metalness: 0.75 });
  const darkSteel = new THREE.MeshStandardMaterial({ color: 0x14181c, roughness: 0.6, metalness: 0.5 });
  const seat = new THREE.MeshStandardMaterial({ color: 0x9aa4ab, roughness: 0.35, metalness: 0.85 });
  disposables.push(steel, darkSteel, seat);

  const mesh = (geo, mat, x, y, z, ry = 0) => {
    disposables.push(geo);
    const m = new THREE.Mesh(geo, mat);
    m.position.set(x, y, z);
    m.rotation.y = ry;
    scene.add(m);
    return m;
  };

  // benches against the walls
  const bench = (x, z, ry) => {
    const g = new THREE.Group();
    g.position.set(x, 0, z);
    g.rotation.y = ry;
    const s = new THREE.Mesh(new THREE.BoxGeometry(1.7, 0.06, 0.45), seat);
    s.position.set(0, 0.46, 0);
    const b = new THREE.Mesh(new THREE.BoxGeometry(1.7, 0.42, 0.05), seat);
    b.position.set(0, 0.72, -0.22);
    b.rotation.x = -0.12;
    g.add(s, b);
    [-0.7, 0.7].forEach((lx) => {
      const leg = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.46, 0.4), darkSteel);
      leg.position.set(lx, 0.23, 0);
      g.add(leg);
      disposables.push(leg.geometry);
    });
    disposables.push(s.geometry, b.geometry);
    scene.add(g);
  };
  bench(-2.62, -4.4, Math.PI / 2);
  bench(-2.62, -6.6, Math.PI / 2);
  bench(2.62, -3.9, -Math.PI / 2);
  bench(2.62, -6.3, -Math.PI / 2);

  // vending machines (green + blue) — front faces the platform, backs against the far wall beside the train
  [
    ["#17a34a", -2.45],
    ["#1a5fd0", 2.45],
  ].forEach(([accent, x]) => {
    const z = -8.75;
    mesh(new THREE.BoxGeometry(0.75, 1.85, 0.6), steel, x, 0.925, z);
    const bandMat = new THREE.MeshStandardMaterial({ color: accent, roughness: 0.4, emissive: accent, emissiveIntensity: 0.35 });
    disposables.push(bandMat);
    mesh(new THREE.BoxGeometry(0.77, 0.26, 0.62), bandMat, x, 1.72, z);
    const tex = vendingTex(accent);
    const faceMat = new THREE.MeshStandardMaterial({
      map: tex,
      emissive: 0xffffff,
      emissiveMap: tex,
      emissiveIntensity: 0.9,
      roughness: 0.4,
    });
    disposables.push(tex, faceMat);
    mesh(new THREE.PlaneGeometry(0.68, 1.8), faceMat, x, 0.95, z + 0.305);
    const glow = new THREE.PointLight(0xdfefff, 3, 3, 2);
    glow.position.set(x, 1.3, z + 0.7);
    scene.add(glow);
  });

  // fashion magazine lightbox on the right wall
  const loader = new THREE.TextureLoader();
  const srgb = (url) => {
    const t = loader.load(url);
    t.colorSpace = THREE.SRGBColorSpace;
    t.anisotropy = 8;
    disposables.push(t);
    return t;
  };
  const magTex = srgb(posterUrl);
  const pMat = new THREE.MeshStandardMaterial({
    map: magTex,
    emissive: 0xffffff,
    emissiveMap: magTex,
    emissiveIntensity: 0.22,
    roughness: 0.3,
  });
  disposables.push(pMat);
  mesh(new THREE.BoxGeometry(0.08, 1.75, 1.2), darkSteel, 2.86, 2.55, -1.6);
  mesh(new THREE.PlaneGeometry(1.1, 1.65), pMat, 2.81, 2.55, -1.6, -Math.PI / 2);

  // magazines scattered on benches / floor
  const magSide = new THREE.MeshStandardMaterial({ color: 0xe8e8e8, roughness: 0.9 });
  const magCover = new THREE.MeshStandardMaterial({ map: magTex, roughness: 0.4 });
  disposables.push(magSide, magCover);
  const magGeo = new THREE.BoxGeometry(0.22, 0.012, 0.31);
  disposables.push(magGeo);
  [
    [-2.62, 0.5, -4.15, 0.4],
    [-2.55, 0.512, -4.05, -0.2],
    [2.6, 0.5, -3.75, 1.2],
    [-1.75, 0.006, -6.4, 0.9],
    [0.9, 0.006, -2.4, -0.6],
  ].forEach(([x, y, z, ry]) => {
    const m = new THREE.Mesh(magGeo, [magSide, magSide, magCover, magSide, magSide, magSide]);
    m.position.set(x, y, z);
    m.rotation.y = ry;
    scene.add(m);
  });

  // rain puddles: real planar reflections of the neon + tubes
  const puddle = (x, z, sx, sz, rot) => {
    const geo = new THREE.CircleGeometry(1, 26);
    const pos = geo.attributes.position;
    for (let i = 1; i < pos.count; i++) {
      const k = 0.78 + Math.random() * 0.3;
      pos.setXY(i, pos.getX(i) * k, pos.getY(i) * k);
    }
    const r = new Reflector(geo, { clipBias: 0.003, textureWidth: 384, textureHeight: 384, color: 0x6f7f86 });
    r.layers.set(1);
    r.rotation.x = -Math.PI / 2;
    r.rotation.z = rot;
    r.scale.set(sx, sz, 1);
    r.position.set(x, 0.012, z);
    scene.add(r);
    disposables.push(geo, { dispose: () => r.dispose() });
  };
  puddle(0.7, -7.2, 1.7, 0.85, 0.3);
  puddle(-1.35, -4.9, 1.15, 0.6, -0.5);
  puddle(1.9, -3.3, 0.9, 0.5, 0.9);

  // ceiling pipes + conduit
  const pipeGeo = new THREE.CylinderGeometry(0.06, 0.06, 15, 10);
  disposables.push(pipeGeo);
  [-2.2, -1.95, 2.3].forEach((x, i) => {
    const p = new THREE.Mesh(pipeGeo, i === 2 ? darkSteel : steel);
    p.rotation.x = Math.PI / 2;
    p.position.set(x, 4.95 - (i === 1 ? 0.14 : 0), -1.4);
    scene.add(p);
  });

  // trash bin + fire extinguisher box
  mesh(new THREE.CylinderGeometry(0.22, 0.2, 0.78, 14), steel, 2.55, 0.39, -2.6);
  const redMat = new THREE.MeshStandardMaterial({ color: 0xc41e2a, roughness: 0.5 });
  disposables.push(redMat);
  mesh(new THREE.BoxGeometry(0.06, 0.6, 0.32), redMat, -2.86, 1.3, -2.2);

  // tactile paving strip near the platform edge
  const tactile = new THREE.MeshStandardMaterial({ color: 0xd9b829, roughness: 0.85 });
  disposables.push(tactile);
  mesh(new THREE.BoxGeometry(6.4, 0.012, 0.32), tactile, 0, 0.006, -8.3);

  // chrome guard railings along the platform edge (gap at the centre door)
  const chrome = new THREE.MeshStandardMaterial({ color: 0xc9d2d8, roughness: 0.25, metalness: 0.95 });
  disposables.push(chrome);
  const railing = (x0, x1, z) => {
    const w = x1 - x0;
    const cx = (x0 + x1) / 2;
    [0.9, 0.62, 0.36].forEach((y) => mesh(new THREE.CylinderGeometry(0.018, 0.018, w, 10), chrome, cx, y, z).rotation.z = Math.PI / 2);
    for (let x = x0; x <= x1 + 0.001; x += w / 3) mesh(new THREE.CylinderGeometry(0.024, 0.024, 0.92, 10), chrome, x, 0.46, z);
    for (let x = x0 + 0.12; x < x1; x += 0.12) mesh(new THREE.CylinderGeometry(0.007, 0.007, 0.54, 6), chrome, x, 0.49, z);
  };
  railing(-3.05, -0.95, -8.15);
  railing(0.95, 3.05, -8.15);

  // grime: dark blotches over the landing so the ground reads dirty and matte
  const grimeTex = canvasTex(1024, 1024, (g, w, h) => {
    for (let i = 0; i < 260; i++) {
      const r = 20 + Math.random() * 120;
      const grad = g.createRadialGradient(0, 0, 0, 0, 0, r);
      grad.addColorStop(0, `rgba(8,10,10,${0.35 + Math.random() * 0.4})`);
      grad.addColorStop(1, "rgba(8,10,10,0)");
      g.save();
      g.translate(Math.random() * w, Math.random() * h);
      g.scale(1, 0.4 + Math.random() * 0.8);
      g.fillStyle = grad;
      g.fillRect(-r, -r, r * 2, r * 2);
      g.restore();
    }
  });
  const grimeMat = new THREE.MeshBasicMaterial({ map: grimeTex, transparent: true, depthWrite: false, opacity: 0.9 });
  disposables.push(grimeTex, grimeMat);
  const grime = mesh(new THREE.PlaneGeometry(6.4, 6), grimeMat, 0, 0.009, -5.9);
  grime.rotation.x = -Math.PI / 2;
}
